import { GetClientsParams, GetClientsResponse } from "@/redux/apis/client/types";
import { GetVehiclesParams } from "@/redux/apis/vehicle/types";
import { OnboardingStatus, SectionStatus } from "@/redux/slices/onboarding/types";
import { GetVehiclesResponse, Vehicle } from "@/redux/slices/vehicle/types";
import { ArchivedStatuses } from "../../Clients/types";

/**
 * Shared contract for the Get Started steps, mirrored by the Driver App: the Stripe gate on steps 2-6
 * and completion for steps 3-6. Kept free of React and RTK Query so either surface can use it verbatim.
 */

/** `VERIFIED | UNVERIFIED | EXPIRED` — web-app `VehicleRegistration.php`. */
export const VERIFIED_REGISTRATION_STATUS = "VERIFIED";

/**
 * `account_status` only accepts `active|removed`, but pending states are still returned — see
 * `PendingStatuses` in `organisms/Clients/types.ts`. Filtering to `active` would miss an
 * invited-but-unaccepted client, which is the case step 4's copy describes, so the rows are judged
 * here instead. Terminal statuses come from {@link ArchivedStatuses} rather than a second list, so
 * the two cannot drift.
 */
export const isArchivedClientStatus = (status: string): boolean => ArchivedStatuses.includes(status);

/** Raw section keys the Driver App PATCHes; web reads them via the typed `gettingStarted` fields. */
export const SOLO_PRICING_VISITED_SECTION = "solo_pricing_visited";
export const SOLO_NETWORK_VISITED_SECTION = "solo_network_visited";

/**
 * Narrows to non-draft, non-archived vehicles; the rest of the rule is applied client-side because
 * no query param expresses it.
 *
 * Deliberately no `is_registration_verified`: that param is a *roadworthy* predicate (current rego
 * expiry plus approved insurance), not `registration.status`, and it ignores
 * `registration_last_verified_date` — a row can pass it and still serialize as `UNVERIFIED`.
 *
 * Deliberately no `owned_by: "PROVIDER"` either, though the spec suggested it: server-side that
 * becomes `provider.provider_uuid IS NOT NULL`, and a solo operator's vehicle is held against the
 * owner account, so it excludes the very vehicle this step is about — verified against staging,
 * where it returned zero rows for an otherwise-qualifying vehicle.
 */
export const vehicleStepParams: GetVehiclesParams = {
  setup_incomplete: false,
  is_archived: false,
  page_size: "25",
};

/**
 * No `account_status`: the backend applies that filter only when non-empty, so omitting it returns
 * every status. A page rather than one row, because a lone `removed` row would otherwise read as
 * "no clients".
 */
export const clientStepParams: GetClientsParams = {
  page_size: "25",
};

const isApprovedInsurance = (document: Vehicle["documents"][number], now: Date): boolean =>
  document.documentType === "INSURANCE" &&
  document.approvalStatus === "APPROVED" &&
  (document.expiryDate == null || new Date(document.expiryDate) > now);

/**
 * Failing any one of these makes the vehicle a draft, matching the Driver App's `isDraft` heuristic:
 * a finished wizard with missing or unapproved insurance must not tick the step.
 */
export const isVehicleStepEligible = (vehicle: Vehicle, now: Date = new Date()): boolean =>
  vehicle.setupStep === null &&
  vehicle.metadata?.isArchived === false &&
  vehicle.registration?.status === VERIFIED_REGISTRATION_STATUS &&
  (vehicle.capacity?.pax ?? 0) > 0 &&
  (vehicle.documents ?? []).some((document) => isApprovedInsurance(document, now));

/** Rows rather than `page.total`: the server filter cannot express the whole rule. */
export const isVehicleStepComplete = (data?: GetVehiclesResponse, now: Date = new Date()): boolean =>
  data?.vehicles.some((vehicle) => isVehicleStepEligible(vehicle, now)) ?? false;

/** Rows rather than `page.total`: the query is unfiltered, so a count would include ended relationships. */
export const isClientStepComplete = (data?: GetClientsResponse): boolean =>
  data?.data.clients.some((client) => !isArchivedClientStatus(client.status)) ?? false;

export const isSoloStripeValid = (onboardingStatus?: OnboardingStatus): boolean => onboardingStatus?.gettingStarted.stripe.isValid ?? false;

/**
 * `isCompleted` alone, not `isSectionComplete`: the API forces `isValid` true for visit markers, and
 * the marker is absent entirely on an API older than web-app `4f377f455`.
 */
const isVisited = (marker?: SectionStatus): boolean => marker?.isCompleted === true;

export const isPricingStepComplete = (onboardingStatus?: OnboardingStatus): boolean =>
  isVisited(onboardingStatus?.gettingStarted.soloPricingVisited);

export const isNetworkStepComplete = (onboardingStatus?: OnboardingStatus): boolean =>
  isVisited(onboardingStatus?.gettingStarted.soloNetworkVisited);
